<template>
    <!-- 20260606 -- Copilot (Claude Sonnet 4.6) -- System Health: stacked signals, per-signal color coding -->
    <div class="system-health">

        <!-- Loading -->
        <div v-if="loading" class="health-skeleton"></div>

        <!-- Clean: single calm line -->
        <div v-else-if="healthData && healthData.clean" class="text-muted small">
            Everything's up to date.
        </div>

        <!-- Active signals: one per row, each with its own color -->
        <div v-else-if="healthData && activeSignals.length > 0" class="health-signal-list">
            <div
                v-for="signal in activeSignals"
                :key="signal.key"
                class="health-signal-row"
                role="button"
                tabindex="0"
                @click="$emit('navigate', signal.key)"
                @keydown.enter.prevent="$emit('navigate', signal.key)"
                @keydown.space.prevent="$emit('navigate', signal.key)"
            >
                <span :class="['health-signal-text', signal.cls]">{{ signal.text }}</span>
            </div>
        </div>

    </div>
</template>

<script>
// 20260606 -- Copilot (Claude Sonnet 4.6) -- System Health: stacked signals, distinct colors per type
import moment from 'moment'

export default {
    name: 'SystemHealth',

    props: {
        healthData: {
            type: Object,
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        }
    },

    emits: ['navigate'],

    computed: {
        activeSignals() {
            if (!this.healthData) return []
            const { overdue, noNextAction, waitingOnOthers, somedayToReview } = this.healthData
            const signals = []

            if (overdue?.count > 0) {
                signals.push({
                    key: 'overdue',
                    text: `${overdue.count} past due`,
                    cls: 'health-signal-text--overdue'
                })
            }

            if (noNextAction?.show && noNextAction?.count > 0) {
                const n = noNextAction.count
                signals.push({
                    key: 'noNextAction',
                    text: `${n} ${n === 1 ? 'folder needs' : 'folders need'} a next action`,
                    cls: 'health-signal-text--no-next-action'
                })
            }

            if (waitingOnOthers?.show && waitingOnOthers?.count > 0) {
                const n = waitingOnOthers.count
                const days = waitingOnOthers.oldestModifiedMs
                    ? moment().diff(moment(waitingOnOthers.oldestModifiedMs), 'days')
                    : null
                const age = days !== null ? `, oldest untouched ${days}d` : ''
                signals.push({
                    key: 'waitingOnOthers',
                    text: `${n} ${n === 1 ? 'item' : 'items'} waiting on others${age}`,
                    cls: 'health-signal-text--waiting'
                })
            }

            if (somedayToReview?.show && somedayToReview?.count > 0) {
                const n = somedayToReview.count
                signals.push({
                    key: 'somedayToReview',
                    text: `${n} someday ${n === 1 ? 'item' : 'items'} to review`,
                    cls: 'health-signal-text--someday'
                })
            }

            return signals
        }
    }
}
</script>

<style scoped>
.health-skeleton {
    height: 18px;
    width: 180px;
    border-radius: 4px;
    background-color: #e9ecef;
    animation: skeleton-pulse 1.5s ease-in-out infinite;
}

@keyframes skeleton-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.45; }
}

@media (prefers-reduced-motion: reduce) {
    .health-skeleton { animation: none; }
}

/* Fill card body height and center content vertically */
.system-health {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 0;
}

.health-signal-list {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
}

.health-signal-row {
    cursor: pointer;
    line-height: 1.4;
}

.health-signal-row:hover .health-signal-text {
    text-decoration: underline;
}

.health-signal-text {
    font-size: 0.9rem;
    font-weight: 500;
}

.health-signal-text--overdue      { color: #b84040; }
.health-signal-text--no-next-action { color: #c08010; }
.health-signal-text--waiting      { color: #2b6cb0; }
.health-signal-text--someday      { color: #4a5568; }

</style>
